import { Cross2Icon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { ChevronsUpDownIcon } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getTrackingIntervalDisplay,
  TrackingInterval,
} from '@/features/projects/create-subproject/types/tracking-interval';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';

type EditTrackingIntervalDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  currentInterval?: TrackingInterval;
  onIntervalChange: (interval: TrackingInterval) => void;
  children: ReactNode;
};

export const EditTrackingIntervalDialog = ({
  isOpen,
  onClose,
  currentInterval,
  onIntervalChange,
  children,
}: EditTrackingIntervalDialogProps) => {
  const { t } = useTranslation('subproject', { keyPrefix: 'TrackingInterval' });

  const handleIntervalSelect = (interval: TrackingInterval) => {
    onIntervalChange(interval);
    onClose();
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={onClose}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          className="z-50 w-[286px]"
        >
          <div
            className="relative rounded-[16px] border-[0.5px] border-transparent backdrop-blur-md"
            style={{
              background:
                'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), linear-gradient(135.632deg, rgba(68, 0, 155, 0.2) 0%, rgba(35, 112, 202, 0.2) 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            <div className="rounded-[16px] bg-transparent p-[8px]">
              {/* Title Container */}
              <div className="mb-[8px] flex items-center justify-between px-[8px] pb-0 pl-[8px] pr-[6px] pt-[2px]">
                <p className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#fefefe]">
                  {t('title')}
                </p>
                <button
                  onClick={onClose}
                  className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
                >
                  <Cross2Icon className="size-4 text-white" />
                  <span className="sr-only">Close</span>
                </button>
              </div>

              {/* Divider */}
              <div className="mb-[8px] h-[1px] w-full bg-white/50" />

              {/* Content */}
              <div className="px-[8px] pb-[8px]">
                <Select
                  defaultValue={currentInterval ?? undefined}
                  onValueChange={handleIntervalSelect}
                >
                  <SelectTrigger
                    isInputTrigger={false}
                    className="h-9 w-full border border-white/30 bg-white/5 text-[13px] text-white"
                    icon={
                      <ChevronsUpDownIcon className="size-4 stroke-white/60" />
                    }
                    size={'sm'}
                  >
                    <SelectValue asLabelInput={false}>
                      {currentInterval
                        ? getTrackingIntervalDisplay({
                            interval: currentInterval,
                            t,
                          })
                        : t('select')}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent className="text-white">
                    {Object.values(TrackingInterval).map((interval) => (
                      <SelectItem
                        className="text-white"
                        key={interval}
                        value={interval}
                      >
                        {getTrackingIntervalDisplay({
                          interval,
                          t,
                        })}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
